import type { LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

type ListViewEmptyStateProps = {
  icon: LucideIcon;
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
};

export default function ListViewEmptyState({
  icon: Icon,
  title,
  description,
  actionLabel,
  onAction,
}: ListViewEmptyStateProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col items-center justify-center py-24 px-6">
      {/* Grid + Icon Illustration */}
      <div className="relative w-[200px] h-[180px] mb-8">
        {/* Grid background */}
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 200 180" fill="none">
          {[25, 50, 75, 100, 125, 150, 175].map((x) => (
            <line
              key={`v${x}`}
              x1={x}
              y1="15"
              x2={x}
              y2="165"
              stroke="#e5e7eb"
              strokeWidth="0.5"
              strokeDasharray="3 5"
            />
          ))}
          {[25, 50, 75, 100, 125, 150].map((y) => (
            <line
              key={`h${y}`}
              x1="15"
              y1={y}
              x2="185"
              y2={y}
              stroke="#e5e7eb"
              strokeWidth="0.5"
              strokeDasharray="3 5"
            />
          ))}
          <circle cx="25" cy="25" r="2.5" fill="#d1d5db" />
          <circle cx="175" cy="25" r="2.5" fill="#d1d5db" />
          <circle cx="25" cy="155" r="2.5" fill="#d1d5db" />
          <circle cx="175" cy="155" r="2.5" fill="#d1d5db" />
        </svg>
        <Icon className="absolute inset-0 m-auto w-14 h-14 text-gray-300" strokeWidth={1} />
      </div>

      {/* Text */}
      <h3 className="text-lg font-semibold text-gray-900 mb-1.5">{t(title)}</h3>
      <p className="text-sm text-gray-500 text-center max-w-[320px] mb-6">{t(description)}</p>

      {/* CTA Button */}
      {actionLabel && onAction && (
        <button
          type="button"
          onClick={onAction}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-blue-700 transition-colors"
        >
          <Icon className="w-4 h-4" />
          {t(actionLabel)}
        </button>
      )}
    </div>
  );
}
